import * as interviewService from "@/services/interview.service";
import {
  getApiErrorStatus,
  type InterviewListParams,
} from "@/services/interview.service";
import { useQuery } from "@tanstack/react-query";

export const interviewKeys = {
  all: ["interviews"] as const,
  list: (params?: InterviewListParams) =>
    [...interviewKeys.all, "list", params ?? {}] as const,
  detail: (id: string) => [...interviewKeys.all, "detail", id] as const,
  byApplication: (applicationId: string) =>
    [...interviewKeys.all, "application", applicationId] as const,
};

/** 401 logs out via the axios interceptor and 404 is terminal — neither is worth a retry. */
const retryUnlessTerminal = (failureCount: number, error: unknown) => {
  const status = getApiErrorStatus(error);
  if (status === 401 || status === 404) return false;
  return failureCount < 1;
};

export const useInterviewsQuery = (params?: InterviewListParams) =>
  useQuery({
    queryKey: interviewKeys.list(params),
    queryFn: () => interviewService.fetchInterviews(params),
    retry: retryUnlessTerminal,
  });

export const useInterviewByIdQuery = (id?: string) =>
  useQuery({
    queryKey: interviewKeys.detail(id ?? ""),
    queryFn: () => interviewService.fetchInterviewById(id!),
    enabled: !!id,
    retry: retryUnlessTerminal,
  });

export const useInterviewsByApplicationQuery = (
  applicationId?: string,
  enabled = true
) =>
  useQuery({
    queryKey: interviewKeys.byApplication(applicationId ?? ""),
    queryFn: () => interviewService.fetchInterviewsByApplication(applicationId!),
    enabled: enabled && !!applicationId,
    retry: retryUnlessTerminal,
  });
